import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { useEditorStore, EditorTraining, EditorSequence } from '~/shell/Store';
import { usePreviewTranslationStore } from '~/shell/PreviewTranslationStore';
import { Button } from '~/ui/buttons';
import LightButton from '~/editor/buttons/LightButton';
import ExitIcon from '~/assets/button/close.svg';
import AiIcon from '~/assets/button/ai.svg';
import SpeakerIcon from '~/assets/button/narration.svg';
import SlideNarrationIcon from '~/assets/button/slide_narration.svg';
import OpenPageIcon from '~/assets/button/openPage.svg';
import SingleIcon from '~/assets/button/singleMode.svg';
import PreviewIcon from '~/assets/button/previewMode.svg';
import TranslateIcon from '~/assets/button/translate.svg';
import UndoIcon from '~/assets/button/undo.svg';
import RedoIcon from '~/assets/button/redo.svg';
import NarrationPopup from '~/editor/narration/NarrationPopup';
import { useMessageStore } from '~/editor/store/messageStore';
import MicIcon from '~/assets/editor/mic.svg';
import AIPromptPopup from '~/editor/ai-prompt/AIPromptPopup';
import { usePreview } from '~/editor/hooks/usePreview';
import { usePendingPositionChanges } from '~/editor/hooks/usePendingPositionChanges';
import { Tooltip } from '~/ui/tooltip/Tooltip';
import { Modal } from '~/ui/modal/Modal';
import { Overlay } from '~/ui/overlay/Overlay';
import { x } from '@xstyled/styled-components';
import { getTrainingLocales } from '../translation/getTrainingLocales';
import ModalCloseButton from '~/ui/buttons/ModalCloseButton';
import buildDashboardUrl from '~/lib/url/buildDashboardUrl';
import navigateWithFullPageReload from '~/lib/navigations/navigateWithFullPageReload';
import { useToastStore } from '~/ui/toast/toastStore';
import { ToastSeverity } from '~/ui/toast/types';
import {
  getTrainingSequences,
  hasTrainingTranslationGaps,
  type TranslationTraining
} from './translationGaps';
import { useExitPublishFlow } from './useExitPublishFlow';
import { isTrainingContentInSync } from '../trainingContentTimestamps';
import { DEFAULT_LOCALE } from '~/lib/translations/defaultLocale';

export const ExitIconForBtn = styled(ExitIcon)`
  position: relative;
  max-height: 22px !important;
  margin-top: -4px !important;

  svg {
    path {
      fill: #9845fb !important;
    }
  }
`;

export const SingleIconForBtn = styled(SingleIcon)``;
export const PreviewIconForBtn = styled(PreviewIcon)``;
export const TranslateIconForBtn = styled(TranslateIcon)`
  width: 20px;
  height: 20px;
  max-width: 20px;
  max-height: 20px;
  display: block;
  flex-shrink: 0;
  /* The SVG has an off-center viewBox; nudge visually */
  transform: translateX(-1px);
`;

const PreviewButtonWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const PublishButtonWrapper = styled.div`
  position: relative;
  display: inline-block;

  /* A disabled button swallows pointer events in some browsers (Chrome), so
     hovering it never reaches the wrapper and the tooltip stays hidden. Let the
     hover pass through to the wrapper — the button is disabled anyway. */
  & button:disabled {
    pointer-events: none;
  }

  &:hover .publishTooltip {
    opacity: 1;
    visibility: visible;
  }
`;

const PublishTooltip = styled.div`
  position: absolute;
  top: calc(100% + 12px);
  right: 24px;
  width: max-content;
  /* max-width: 220px; */
  max-width: 170px;
  padding: 12px 18px;
  background-color: #333a44;
  color: #fff;
  font-family: default;
  font-size: 16px;
  line-height: 1.3;
  text-align: center;
  border-radius: 10px;
  z-index: 100;
  pointer-events: none;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.15s ease;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.18);

  ::after {
    content: '';
    position: absolute;
    top: -6px;
    right: 24px;
    width: 14px;
    height: 14px;
    background-color: inherit;
    border-radius: 3px;
    transform: rotate(45deg);
  }
`;

const Wrapper = styled.div`
  width: 100%;
`;

const Space = styled.div`
  flex: 1;
  position: fixed;
  margin-top: 10px;
  margin-left: 10px;

  &.leftSpace {
    background-color: #fff;
    flex: none;
    padding: 8px;
    border-radius: 16px;
    box-shadow: 3px 3px 5px #0000004f;
    z-index: 200;

    button {
      height: 38px;
      width: 38px;
      margin: 0px;
      padding: 10px 0px;
      border-radius: 0px;

      svg {
        display: inline;
        max-height: 20px;
        color: #9845fb;
        path {
          fill: #9845fb;
        }
      }
    }

    /* Exit button is a standalone pill */
    button.exitBtn {
      border-radius: 10px;
    }
    button.active {
      svg {
        color: #fff;
        path {
          fill: #fff;
        }
      }
    }

    button.arrow-left {
      svg {
        margin-top: -8px;
      }
    }

    button.arrow-right {
      svg {
        margin-top: -8px;
      }
    }

    .modeGroup,
    .undoGroup {
      display: inline-flex;
      align-items: center;
    }

    .modeGroup button,
    .undoGroup button {
      border-radius: 0;
    }

    .modeGroup button:first-child,
    .undoGroup button:first-child {
      border-top-left-radius: 10px;
      border-bottom-left-radius: 10px;
    }

    .modeGroup button:last-child,
    .undoGroup button:last-child {
      border-top-right-radius: 10px;
      border-bottom-right-radius: 10px;
    }

    .undoGroup {
      margin-left: 10px;
      overflow: hidden;
      width: 96px;
      opacity: 1;
      transition:
        opacity 0.2s ease,
        width 0.2s ease,
        margin-left 0.2s ease;
    }

    .undoGroup.hidden {
      width: 0;
      opacity: 0;
      margin-left: 0;
      pointer-events: none;
    }
  }

  &.centerSpace {
    flex: 1;
    left: calc(50% - 180px);
    z-index: 9999;

    .message {
      position: absolute;
      left: 10px;
      padding: 9px;
      z-index: 200;

      border-radius: 16px;
      background: #9738ff;
      width: 370px;
      display: flex;
      align-items: center;
      gap: 10px;

      color: var(--Grey-1, #fff);
      font-family: Raleway;
      font-weight: 700;
      font-size: 16px;
      font-style: normal;
      line-height: 150%;

      text-align: center;

      svg {
        margin-left: 20px;
      }
    }
  }

  &.rightSpace {
    flex: none;
    text-align: right;

    z-index: 200;
    right: 20px;

    background-color: #fff;
    padding: 8px;
    border-radius: 16px;
    box-shadow: 3px 3px 5px #0000004f;

    button {
      height: 38px;
      margin-left: 5px;
      margin-right: 3px;
      border-radius: 10px;
      svg {
        display: inline;
      }
    }

    button.ai {
      width: 38px;
      padding: 0px;
      margin-left: 5px;
      margin-right: 3px;
    }

    button.speaker {
      width: 170px;
      padding: 0px;
      padding-left: 5px;
      margin-left: 5px;
      margin-right: 3px;
      font-family: ralewayBold;
      font-size: 14px;

      svg {
        margin-left: 10px;
      }
    }

    button.speaker.active {
      background-color: #9845fb;
      color: #fff;

      svg {
        path {
          fill: #fff;
        }
      }
    }

    .rBtn {
      font-family: ralewayBold;
      font-size: 14px !important;
      transition:
        background-color 0.2s ease,
        color 0.2s ease,
        padding 0.2s ease,
        border-radius 0.2s ease,
        opacity 0.2s ease;

      svg {
        margin-left: 10px;
        margin-top: -3px;
      }
    }

    .rBtn.preview {
      padding-left: 20px;
      padding-right: 15px;
    }

    .rBtn.saveAndClose {
      display: inline-flex;
      height: 38px;
      padding: 6px 20px;
      align-items: center;
      gap: 4px;
      white-space: nowrap;
      border-radius: 8px;
      background: var(--color-accent-dk);
    }

    .rBtn.saveAndClose:disabled {
      background: var(--color-accent-dk);
      color: #fff;
      opacity: 0.75;
      cursor: wait;
    }

    .buttonSlot {
      display: inline-flex;
      align-items: center;
      height: 38px;
      overflow: hidden;
      flex-shrink: 0;
      vertical-align: top;
      opacity: 1;
      transition:
        opacity 0.2s ease,
        width 0.2s ease;
    }

    .buttonSlot.aiSlot {
      width: 46px;
    }

    .buttonSlot.speakerSlot {
      width: 178px;
    }

    .buttonSlot.hidden {
      width: 0;
      min-width: 0;
      opacity: 0;
      pointer-events: none;
    }

    .buttonSlot.hidden button {
      margin-left: 0;
      margin-right: 0;
      padding-left: 0;
      padding-right: 0;
      width: 0;
      min-width: 0;
      border-width: 0;
    }

    .sequenceIdSection {
      font-size: 12px;
      b {
        font-family: ralewayBold;
      }
    }
  }
`;

interface HeaderProps {
  mode: string;
  setMode: (mode: 'editor' | 'preview' | 'translation') => void;
  exitRedirectUrl: string;
  publishButtonTitle?: string | null;
  publishRedirectUrl?: string | null;
}

const TranslationAutoUpdateButton = ({
  onClick,
  disabled
}: {
  onClick: () => void;
  disabled?: boolean;
}) => {
  return (
    <Button
      fontFamily="ralewayBold"
      fontSize="14px !important"
      onClick={onClick}
      disabled={disabled}
    >
      Auto-Update
    </Button>
  );
};

const SkipTranslationAutoUpdateButton = ({
  onClick,
  disabled
}: {
  onClick: () => void;
  disabled?: boolean;
}) => {
  return (
    <LightButton
      fontFamily="ralewayBold"
      fontSize="14px !important"
      backgroundColor="transparent"
      onClick={onClick}
      disabled={disabled}
    >
      Update Later
    </LightButton>
  );
};

type TrainingForTranslationModal = {
  status?: 'live' | 'draft' | string;
};

const UpdateTranslationsModal = ({
  open,
  onClose,
  onAutoUpdate,
  onUpdateLater,
  training,
  isLoading
}: {
  open: boolean;
  onClose: () => void;
  onAutoUpdate: () => Promise<void> | void;
  onUpdateLater: () => Promise<void> | void;
  training: TrainingForTranslationModal | null;
  isLoading: boolean;
}) => {
  if (!open) return null;

  const isAssign = training?.status === 'live';
  const handleClose = () => {
    if (isLoading) return;
    onClose();
  };

  return (
    <Overlay opacity={0.5} open={open} onClick={handleClose}>
      <Modal open borderRadius="21px">
        <x.div maxWidth="600px" paddingTop="1.5rem">
          <ModalCloseButton onClick={handleClose} />

          <x.div padding="20px 60px 30px">
            <x.h2 textAlign="center" paddingBottom="1rem">
              Update translations before {isAssign ? 'assigning' : 'publishing'}
              ?
            </x.h2>

            <x.p textAlign="center" paddingBottom="1rem">
              Some translations may be outdated or missing. To ensure accuracy,
              select how you would like to update them. Any manually edited
              translations will remain unchanged.
            </x.p>

            <x.div display="flex" justifyContent="center" gap="1rem">
              <TranslationAutoUpdateButton
                onClick={() => {
                  void onAutoUpdate();
                }}
                disabled={isLoading}
              />
            </x.div>

            <x.p textAlign="center" paddingTop="1rem">
              <SkipTranslationAutoUpdateButton
                onClick={() => {
                  void onUpdateLater();
                }}
                disabled={isLoading}
              />
            </x.p>
          </x.div>
        </x.div>
      </Modal>
    </Overlay>
  );
};

const TRANSLATION_STATUS_CHECK_ERROR_MESSAGE =
  'Unable to verify translation status. Please try again.';

export const Header = ({
  mode,
  setMode,
  exitRedirectUrl,
  publishButtonTitle,
  publishRedirectUrl
}: HeaderProps) => {
  const {
    apiService,
    sequence,
    training,
    undo,
    redo,
    history,
    setSequence,
    setTraining,
    markTrainingPublished,
    forceUpdateSequence,
    setForceUpdateSequence,
    nextSequence,
    prevSequence,
    hasFeatureFlag,
    canUndo: storeCanUndo,
    canRedo: storeCanRedo,
    setUndoRedoState,
    undoAction,
    redoAction,
    shouldDisplayUpdateTranslationsModal,
    setDisplayUpdateTranslationsModal,
    setShouldAutoUpdateTranslations,
    setTranslationScope,
    contentEditable
  } = useEditorStore();
  const { setPreviewTranslationLocale } = usePreviewTranslationStore();
  const [showNarration, setNarration] = useState(false);
  const { showMessage, message } = useMessageStore();
  const [showAIPromptPopup, setAIPromptPopup] = useState(false);
  const { openPreview } = usePreview();
  const pendingPositionChanges = usePendingPositionChanges();
  const [showPreviewTooltip, setShowPreviewTooltip] = useState(false);

  const queryParams = new URLSearchParams(location.search);
  const showPublishButton = queryParams.get('sequence_id') ? false : true;

  const canShowTranslationView = useMemo(() => {
    if (!training) return false;
    const locales = getTrainingLocales(training);
    return locales.some(localeOption => localeOption.locale !== 'en');
  }, [training]);

  const hasTranslationGaps = useMemo(() => {
    return hasTrainingTranslationGaps(
      training as unknown as TranslationTraining
    );
  }, [training]);

  const shouldCheckTranslationsBeforeExit = useMemo(() => {
    if (!training) return false;

    return (
      training.status === 'live' &&
      training.shouldDisplayTranslateButton &&
      canShowTranslationView
    );
  }, [training, canShowTranslationView]);

  useEffect(() => {
    if (training && mode === 'translation' && !canShowTranslationView) {
      setMode('editor');
    }
  }, [mode, canShowTranslationView, setMode, training]);

  const shouldAllowAIButton = useMemo(() => {
    return (
      sequence &&
      [
        'Multimedia4',
        'Quiz2',
        'SocialMedia1',
        'Statement2',
        'Statement5'
      ].includes(sequence.templateName as string)
    );
  }, [sequence?.templateName]);

  const [isUndoDisabled, setIsUndoDisabled] = useState(false);
  const [isRedoDisabled, setIsRedoDisabled] = useState(false);
  const [isPublishBlocked, setIsPublishBlocked] = useState(false);
  const [hasMadeEdits, setHasMadeEdits] = useState(false);

  const showTranslationStatusCheckError = () => {
    useToastStore.getState().add({
      severity: ToastSeverity.Error,
      message: TRANSLATION_STATUS_CHECK_ERROR_MESSAGE
    });
  };

  const handleUndo = async () => {
    if (isUndoDisabled) return;

    setIsRedoDisabled(true);
    setIsUndoDisabled(true);
    setForceUpdateSequence(false);

    const useBackendUndoRedo = hasFeatureFlag(
      'course_editor_backend_undo_redo'
    );

    if (useBackendUndoRedo && sequence) {
      try {
        await undoAction(
          sequence.id,
          getSequenceInfo as (
            sequenceId: number,
            training: EditorTraining
          ) => void
        );
      } catch (error) {
        console.error('Undo failed:', error);
        setIsUndoDisabled(false);
        setIsRedoDisabled(false);
      }
    } else {
      undo(
        getSequenceInfo as (
          sequenceId: number,
          training: EditorTraining
        ) => void
      );
    }
  };

  const handleRedo = async () => {
    if (isRedoDisabled) return;

    setIsRedoDisabled(true);
    setIsUndoDisabled(true);
    setForceUpdateSequence(false);

    const useBackendUndoRedo = hasFeatureFlag(
      'course_editor_backend_undo_redo'
    );

    if (useBackendUndoRedo && sequence) {
      try {
        await redoAction(
          sequence.id,
          getSequenceInfo as (
            sequenceId: number,
            training: EditorTraining
          ) => void
        );
      } catch (error) {
        console.error('Redo failed:', error);
        setIsUndoDisabled(false);
        setIsRedoDisabled(false);
      }
    } else {
      redo(
        getSequenceInfo as (
          sequenceId: number,
          training: EditorTraining
        ) => void
      );
    }
  };

  const getSequenceInfo = (
    sequenceId: number,
    trainingList: TranslationTraining
  ) => {
    if (apiService) {
      apiService
        .getSequenceInfo(sequenceId)
        .then(
          (sequenceResponse: {
            sequence: { id: number };
            sequenceMetadata?: { canUndo: boolean; canRedo: boolean };
          }) => {
            setSequence(sequenceResponse.sequence as EditorSequence);

            if (
              sequenceResponse.sequenceMetadata &&
              hasFeatureFlag('course_editor_backend_undo_redo')
            ) {
              setUndoRedoState(
                sequenceResponse.sequenceMetadata.canUndo,
                sequenceResponse.sequenceMetadata.canRedo
              );
            }

            const updatedTraining: TranslationTraining = {
              ...trainingList,
              chapters: (trainingList.chapters || []).map(chapter => ({
                ...chapter,
                chapterSequences: chapter?.chapterSequences?.map(
                  chapterSequence => {
                    return chapterSequence.sequence?.id === sequenceId
                      ? {
                          ...chapterSequence,
                          sequence: {
                            ...chapterSequence.sequence,
                            ...sequenceResponse.sequence
                          }
                        }
                      : chapterSequence;
                  }
                )
              }))
            };
            setTraining(updatedTraining as EditorTraining);
            setForceUpdateSequence(true);

            setIsUndoDisabled(false);
            setIsRedoDisabled(false);
          }
        );
    }
  };

  const { isExitInProgress, doPublish, publishFromModal, onExit } =
    useExitPublishFlow({
      apiService,
      training: training as unknown as TranslationTraining | null,
      publishRedirectUrl,
      exitRedirectUrl,
      shouldCheckTranslationsBeforeExit:
        shouldCheckTranslationsBeforeExit as boolean,
      hasTranslationGaps,
      setTraining: setTraining as unknown as (
        training: TranslationTraining
      ) => void,
      markTrainingPublished,
      setDisplayUpdateTranslationsModal,
      setShouldAutoUpdateTranslations,
      onError: showTranslationStatusCheckError
    });

  const onPublishClick = async () => {
    if (!training) return;

    if (training.canAssignLearners) return doPublish(false);

    const redirectUrl = buildDashboardUrl(
      '/companies/:companyId/payment_methods',
      {
        pathParams: {
          companyId: training.companyId,
          trainingId: training.id
        },
        queryParams: {
          mode: 'upgrade_lms',
          plan_package: 'lms_package',
          training_id: training.id,
          upgrade: true
        }
      }
    );

    return navigateWithFullPageReload(redirectUrl);
  };

  const canWeDoRedo = () => {
    const useBackendUndoRedo = hasFeatureFlag(
      'course_editor_backend_undo_redo'
    );

    if (useBackendUndoRedo) {
      return storeCanRedo;
    }

    if (forceUpdateSequence) {
      return false;
    }

    const currentHistory = history.filter(
      item => item.sequenceId == sequence!.id
    );

    if (currentHistory.length == 0) {
      return false;
    }

    const activeIndex = currentHistory.findIndex(item => item.active === true);
    return activeIndex >= 0 && activeIndex < currentHistory.length - 1;
  };

  const canWeDoUndo = () => {
    const useBackendUndoRedo = hasFeatureFlag(
      'course_editor_backend_undo_redo'
    );

    if (useBackendUndoRedo) {
      return storeCanUndo;
    }

    if (forceUpdateSequence) {
      return false;
    }

    const currentHistory = history.filter(
      item => item.sequenceId == sequence!.id
    );

    if (currentHistory.length == 0) {
      return false;
    }

    const activeIndex = currentHistory.findIndex(item => item.active === true);
    return activeIndex > 0;
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const activeElement = document.activeElement;
      const isInputElement =
        activeElement?.tagName === 'INPUT' ||
        activeElement?.tagName === 'TEXTAREA' ||
        (activeElement as HTMLElement)?.contentEditable === 'true' ||
        activeElement?.getAttribute('contenteditable') === 'true';

      if (isInputElement) {
        return;
      }

      if (!training || !sequence) return;

      const allSequences = getTrainingSequences(
        training as unknown as TranslationTraining
      );

      if (allSequences.length === 0) return;

      switch (event.key) {
        case 'ArrowDown':
        case 'ArrowRight':
          if (sequence.id === allSequences[allSequences.length - 1].id) {
            return;
          }
          event.preventDefault();
          nextSequence();
          break;
        case 'ArrowUp':
        case 'ArrowLeft':
          if (sequence.id === allSequences[0].id) {
            return;
          }
          event.preventDefault();
          prevSequence();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [training, sequence]);

  useEffect(() => {
    const blockPublish = Boolean(
      training &&
      training.status === 'draft' &&
      training.customizationOfCanonical &&
      isTrainingContentInSync(training)
    );

    setIsPublishBlocked(blockPublish);

    if (!blockPublish) {
      setHasMadeEdits(true);
    }
  }, [training]);

  const getPublishButtonTitle = () => {
    if (publishButtonTitle) return publishButtonTitle;
    if (training!.status !== 'live') return 'Publish';
    if (training!.canAssignLearners) return 'Assign';

    return 'Upgrade to Assign';
  };

  const getPrimaryButtonLabel = () => {
    // "Saving..." only while an actual save/publish is running — not when
    // publish is merely blocked because the custom draft has no edits (CAT-677).
    if (isExitInProgress) return 'Saving...';
    if (mode === 'translation') return 'Save and Close';
    return getPublishButtonTitle();
  };

  const isLiveTraining = training?.status === 'live';
  const isEditorMode = mode === 'editor';
  const actionButtonClassName =
    mode === 'translation' ? 'rBtn saveAndClose' : 'rBtn';

  const onPrimaryButtonClick = () => {
    if (mode === 'translation') {
      setMode('editor');
      return;
    }

    if (isLiveTraining && training?.canAssignLearners) {
      onExit({ requirePublish: true });
      return;
    }

    onPublishClick();
  };

  const onEditorModeSwitchClick = () => {
    setPreviewTranslationLocale(DEFAULT_LOCALE);
    setMode('editor');
  };

  return (
    <Wrapper>
      <Space className="leftSpace">
        <LightButton
          className="exitBtn"
          onClick={() => onExit()}
          style={{ marginRight: '10px' }}
        >
          <ExitIconForBtn />
        </LightButton>

        <div className="modeGroup">
          <LightButton
            className={mode === 'editor' ? 'active' : ''}
            onClick={onEditorModeSwitchClick}
          >
            <SingleIconForBtn />
          </LightButton>
          <LightButton
            className={mode === 'preview' ? 'active' : ''}
            onClick={() => setMode('preview')}
          >
            <PreviewIconForBtn />
          </LightButton>
          {canShowTranslationView && (
            <LightButton
              className={mode === 'translation' ? 'active' : ''}
              onClick={() => {
                if (sequence?.id) {
                  setTranslationScope({
                    kind: 'sequence',
                    sequenceId: sequence.id
                  });
                }
                setMode('translation');
              }}
            >
              <TranslateIconForBtn />
            </LightButton>
          )}
        </div>
        <div className={isEditorMode ? 'undoGroup' : 'undoGroup hidden'}>
          <LightButton
            className="arrow arrow-left"
            onClick={handleUndo}
            disabled={!isEditorMode || !canWeDoUndo() || isUndoDisabled}
          >
            <UndoIcon />
          </LightButton>
          <LightButton
            className="arrow arrow-right"
            onClick={handleRedo}
            disabled={!isEditorMode || !canWeDoRedo() || isRedoDisabled}
          >
            <RedoIcon />
          </LightButton>
        </div>
      </Space>
      <Space className="centerSpace">
        {showMessage && (
          <div className="message">
            <MicIcon />
            <p>{message}</p>
          </div>
        )}
      </Space>
      <Space className="rightSpace">
        {shouldAllowAIButton && (
          <div
            className={
              isEditorMode ? 'buttonSlot aiSlot' : 'buttonSlot aiSlot hidden'
            }
          >
            <LightButton
              onClick={() => setAIPromptPopup(true)}
              className="ai"
              disabled={!isEditorMode}
            >
              <AiIcon />
            </LightButton>
          </div>
        )}
        <UpdateTranslationsModal
          open={shouldDisplayUpdateTranslationsModal}
          onClose={() => setDisplayUpdateTranslationsModal(false)}
          onAutoUpdate={() => publishFromModal(true)}
          onUpdateLater={() => publishFromModal(false)}
          training={training}
          isLoading={isExitInProgress}
        />
        {contentEditable &&
          sequence &&
          sequence.templateName != 'Multimedia2' && (
            <div
              className={
                isEditorMode
                  ? 'buttonSlot speakerSlot'
                  : 'buttonSlot speakerSlot hidden'
              }
            >
              <LightButton
                onClick={() => setNarration(!showNarration)}
                className={sequence.narration ? 'speaker active' : 'speaker'}
                disabled={!isEditorMode}
              >
                Slide Narration
                {sequence.narration ? <SlideNarrationIcon /> : <SpeakerIcon />}
              </LightButton>
            </div>
          )}
        <PreviewButtonWrapper
          onMouseEnter={() =>
            pendingPositionChanges > 0 && setShowPreviewTooltip(true)
          }
          onMouseLeave={() => setShowPreviewTooltip(false)}
        >
          <LightButton
            className="rBtn preview"
            onClick={() => openPreview()}
            disabled={pendingPositionChanges > 0}
          >
            Preview
            <OpenPageIcon />
          </LightButton>
          {showPreviewTooltip && pendingPositionChanges > 0 && (
            <Tooltip placement="bottom-center-up" top="50px" right="0">
              Saving changes... Please wait.
            </Tooltip>
          )}
        </PreviewButtonWrapper>
        {showPublishButton ? (
          <PublishButtonWrapper>
            <Button
              className={actionButtonClassName}
              onClick={onPrimaryButtonClick}
              disabled={isExitInProgress || isPublishBlocked}
            >
              {getPrimaryButtonLabel()}
            </Button>
            {isPublishBlocked && !hasMadeEdits && (
              <PublishTooltip className="publishTooltip">
                Edit course to enable publishing
              </PublishTooltip>
            )}
          </PublishButtonWrapper>
        ) : (
          <span className="sequenceIdSection">
            {' '}
            Sequence ID: <b>{sequence?.id}</b>
          </span>
        )}
        {showNarration &&
          contentEditable &&
          createPortal(
            <NarrationPopup onClose={() => setNarration(false)} />,
            document.body
          )}
        {showAIPromptPopup &&
          createPortal(
            <AIPromptPopup onClose={() => setAIPromptPopup(false)} />,
            document.body
          )}
      </Space>
    </Wrapper>
  );
};
